import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Copy, Save, Trophy, Frown, TrendingUp, ArrowLeft, PowerOff, Gift, CheckCircle2, AlertTriangle, Ticket, Ban } from "lucide-react";
import { api, formatApiError } from "@/lib/apiClient";

const REWARD_TYPES = [
  { v: "nothing", l: "Better luck next time" },
  { v: "coins", l: "Pop Coins" },
  { v: "popsicle", l: "Free Popsicle" },
  { v: "popcorn", l: "Free Popcorn" },
  { v: "discount", l: "Discount" },
  { v: "bogo", l: "Buy 1 Get 1" },
  { v: "mystery", l: "Mystery Reward" },
  { v: "festival", l: "Festival Reward" },
  { v: "custom", l: "Custom" },
];

const PALETTE = ["#FFC93C", "#FF4F5A", "#7EC8E3", "#8B5CF6", "#FF8A00", "#B7B7B7", "#FFB300", "#4CAF50", "#2196F3", "#E91E63"];

export default function AdminSpinWheels() {
  const [wheels, setWheels] = useState([]);
  const [current, setCurrent] = useState(null); // full selected wheel
  const [analytics, setAnalytics] = useState(null);
  const [tab, setTab] = useState("segments"); // segments | rules | grants | history

  const load = async () => {
    try {
      const [{ data: ws }, { data: a }] = await Promise.all([
        api.get("/admin/spin/wheels"),
        api.get("/admin/spin/analytics"),
      ]);
      setWheels(ws);
      setAnalytics(a);
      if (ws.length && (!current || !ws.find((w) => w.id === current.id))) {
        setCurrent(ws[0]);
      } else if (current) {
        setCurrent(ws.find((w) => w.id === current.id));
      }
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const createWheel = async () => {
    const name = window.prompt("Wheel name?", "New Wheel");
    if (!name) return;
    try {
      const { data } = await api.post("/admin/spin/wheels", { name });
      toast.success("Wheel created");
      await load();
      setCurrent(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteWheel = async (w) => {
    if (!window.confirm(`Delete wheel "${w.name}" and all its segments?`)) return;
    try {
      await api.delete(`/admin/spin/wheels/${w.id}`);
      toast.success("Deleted");
      setCurrent(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const duplicateWheel = async (w) => {
    try {
      const { data } = await api.post(`/admin/spin/wheels/${w.id}/duplicate`);
      toast.success("Duplicated");
      await load();
      setCurrent(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleActive = async (w) => {
    try {
      await api.put(`/admin/spin/wheels/${w.id}`, { ...w, is_active: !w.is_active });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="admin-spin-page">
      <header className="flex items-end justify-between mb-6">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Engagement</div>
          <h1 className="editorial-h1 text-4xl">Spin &amp; Win</h1>
        </div>
        <button onClick={createWheel} className="btn-pill btn-dark text-xs" data-testid="new-wheel-btn"><Plus size={14} /> New wheel</button>
      </header>

      {/* Analytics strip */}
      {analytics && (
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 mb-6">
          <Stat icon={Trophy} label="Total Spins" value={analytics.total_spins} />
          <Stat icon={CheckCircle2} label="Wins" value={analytics.total_wins} accent />
          <Stat icon={Frown} label="Losses" value={analytics.total_losses} />
          <Stat icon={TrendingUp} label="Win Rate" value={`${analytics.win_rate}%`} />
          <Stat icon={Gift} label="Pending Rewards" value={analytics.pending_spin_rewards} />
          <Stat icon={Ticket} label="Participants" value={analytics.unique_participants} />
        </div>
      )}

      <div className="grid lg:grid-cols-[320px_1fr] gap-6">
        {/* Wheels list */}
        <aside className="rounded-2xl bg-white border border-brand-line overflow-hidden h-fit">
          <div className="px-4 py-3 border-b border-brand-line text-[11px] uppercase tracking-widest text-brand-charcoal/55">Wheels</div>
          {wheels.length === 0 && <div className="p-6 text-center text-brand-charcoal/45 text-sm">No wheels yet. Create your first!</div>}
          <ul>
            {wheels.map((w) => (
              <li key={w.id}
                  onClick={() => setCurrent(w)}
                  data-testid={`wheel-item-${w.id}`}
                  className={`px-4 py-3 cursor-pointer border-b border-brand-line hover:bg-brand-muted/40 ${current?.id === w.id ? "bg-brand-yellow/15" : ""}`}>
                <div className="flex items-center gap-2">
                  <span className="h-3 w-3 rounded-full" style={{ background: w.theme_color }} />
                  <span className="font-medium flex-1 truncate">{w.name}</span>
                  {!w.is_active && <span className="text-[9px] uppercase text-brand-charcoal/45">off</span>}
                </div>
                <div className="mt-1 flex items-center gap-2 text-[11px] text-brand-charcoal/55">
                  <span>{w.segments?.length || 0} segments</span>
                  <span>·</span>
                  <span className={w.probability_valid ? "text-emerald-600" : "text-red-600"}>
                    {w.probability_valid ? "✓ 100%" : `${w.probability_total?.toFixed(1) || 0}%`}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        </aside>

        {/* Wheel detail */}
        {current ? (
          <div>
            {/* Wheel header actions */}
            <div className="rounded-2xl bg-white border border-brand-line p-5 mb-4">
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-display text-2xl">{current.name}</div>
                  <div className="text-xs text-brand-charcoal/55 mt-0.5">{current.description || "No description."}</div>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => toggleActive(current)} data-testid="toggle-wheel-active" className="btn-pill btn-ghost text-xs">
                    <PowerOff size={12} /> {current.is_active ? "Deactivate" : "Activate"}
                  </button>
                  <button onClick={() => duplicateWheel(current)} data-testid="duplicate-wheel-btn" className="btn-pill btn-ghost text-xs"><Copy size={12} /> Duplicate</button>
                  <button onClick={() => deleteWheel(current)} data-testid="delete-wheel-btn" className="btn-pill btn-ghost text-xs text-red-600"><Trash2 size={12} /> Delete</button>
                </div>
              </div>
              {!current.probability_valid && (
                <div className="mt-3 rounded-xl bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-900 inline-flex items-center gap-2">
                  <AlertTriangle size={14} /> Probabilities total <b>{current.probability_total?.toFixed(2)}%</b> — must equal 100% for the wheel to work.
                </div>
              )}
            </div>

            <div className="flex gap-1 border-b border-brand-line mb-4">
              {[["segments", "Segments & Probabilities"], ["rules", "Rules & Eligibility"], ["grants", "Grants"], ["history", "Spin History"]].map(([k, l]) => (
                <button key={k} onClick={() => setTab(k)} data-testid={`spin-tab-${k}`} className={`px-4 py-2 text-sm border-b-2 transition-colors ${tab === k ? "border-brand-charcoal text-brand-charcoal" : "border-transparent text-brand-charcoal/55 hover:text-brand-charcoal"}`}>{l}</button>
              ))}
            </div>

            {tab === "segments" && <SegmentsPanel wheel={current} onReload={load} />}
            {tab === "rules" && <RulesPanel wheel={current} onReload={load} />}
            {tab === "grants" && <GrantsPanel wheel={current} />}
            {tab === "history" && <HistoryPanel wheel={current} />}
          </div>
        ) : (
          <div className="rounded-2xl bg-white border border-brand-line p-10 text-center text-brand-charcoal/45">Select a wheel or create a new one to begin.</div>
        )}
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className={`rounded-2xl border p-4 ${accent ? "bg-brand-charcoal text-brand-cream border-brand-charcoal" : "bg-white border-brand-line"}`}>
      <div className={`text-[10px] uppercase tracking-widest inline-flex items-center gap-1 ${accent ? "text-brand-yellow" : "text-brand-charcoal/55"}`}><Icon size={11} /> {label}</div>
      <div className="text-2xl font-display mt-1">{value ?? 0}</div>
    </div>
  );
}

// -------- Segments panel --------
function SegmentsPanel({ wheel, onReload }) {
  const [editing, setEditing] = useState(null);
  const empty = { label: "", type: "custom", value: "", color: PALETTE[0], probability: 0, is_active: true, sort_order: (wheel.segments?.length || 0), description: "", image_url: "" };

  const save = async (s) => {
    try {
      const payload = { ...s, probability: parseFloat(s.probability) || 0, sort_order: parseInt(s.sort_order) || 0 };
      if (s.id) await api.put(`/admin/spin/segments/${s.id}`, payload);
      else await api.post(`/admin/spin/wheels/${wheel.id}/segments`, payload);
      toast.success("Saved");
      setEditing(null);
      onReload();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (s) => {
    if (!window.confirm(`Delete segment "${s.label}"?`)) return;
    try { await api.delete(`/admin/spin/segments/${s.id}`); toast.success("Deleted"); onReload(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="rounded-2xl bg-white border border-brand-line p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="text-[11px] uppercase tracking-widest text-brand-charcoal/55">Segments ({wheel.segments?.length || 0})</div>
        <button onClick={() => setEditing({ ...empty })} className="btn-pill btn-dark text-xs" data-testid="new-segment-btn"><Plus size={12} /> Add segment</button>
      </div>
      <table className="w-full text-sm">
        <thead className="text-[10px] uppercase tracking-widest text-brand-charcoal/55">
          <tr>
            <th className="text-left py-2">Color</th>
            <th className="text-left py-2">Label</th>
            <th className="text-left py-2">Type</th>
            <th className="text-left py-2">Value</th>
            <th className="text-right py-2">Probability</th>
            <th className="text-right py-2">Actions</th>
          </tr>
        </thead>
        <tbody>
          {(wheel.segments || []).map((s) => (
            <tr key={s.id} className="border-t border-brand-line" data-testid={`segment-row-${s.id}`}>
              <td className="py-2"><span className="inline-block h-5 w-5 rounded-full border border-brand-line" style={{ background: s.color }} /></td>
              <td className="py-2">{s.label} {!s.is_active && <span className="ml-1 text-[10px] uppercase text-brand-charcoal/45">hidden</span>}</td>
              <td className="py-2 capitalize text-brand-charcoal/70">{s.type}</td>
              <td className="py-2 font-mono">{s.value || "—"}</td>
              <td className="py-2 text-right font-mono">{s.probability}%</td>
              <td className="py-2 text-right">
                <button onClick={() => setEditing(s)} data-testid={`edit-segment-${s.id}`} className="text-xs text-brand-charcoal/70 hover:text-brand-charcoal inline-flex items-center gap-1 mr-3"><Pencil size={12} /> Edit</button>
                <button onClick={() => del(s)} data-testid={`delete-segment-${s.id}`} className="text-xs text-red-600 hover:text-red-800 inline-flex items-center gap-1"><Trash2 size={12} /> Delete</button>
              </td>
            </tr>
          ))}
          {(!wheel.segments || wheel.segments.length === 0) && (
            <tr><td colSpan={6} className="py-8 text-center text-brand-charcoal/45">No segments yet.</td></tr>
          )}
        </tbody>
      </table>

      {editing && <EditSegmentModal value={editing} onClose={() => setEditing(null)} onSave={save} />}
    </div>
  );
}

function EditSegmentModal({ value, onClose, onSave }) {
  const [f, setF] = useState(value);
  const update = (k) => (e) => {
    const v = e.target?.type === "checkbox" ? e.target.checked : e.target.value;
    setF((p) => ({ ...p, [k]: v }));
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4">
      <form onSubmit={(e) => { e.preventDefault(); onSave(f); }} className="w-full max-w-lg rounded-2xl bg-white p-6 space-y-3" data-testid="segment-edit-modal">
        <div className="text-[11px] uppercase tracking-widest text-brand-charcoal/55">{f.id ? "Edit segment" : "New segment"}</div>
        <div><label className="text-[11px] uppercase tracking-widest text-brand-charcoal/55">Label</label><input required value={f.label} onChange={update("label")} className="pp-input" data-testid="segment-label-input" /></div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-[11px] uppercase tracking-widest text-brand-charcoal/55">Type</label>
            <select value={f.type} onChange={update("type")} className="pp-input" data-testid="segment-type-select">
              {REWARD_TYPES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-widest text-brand-charcoal/55">Value (coins/discount/etc)</label>
            <input value={f.value || ""} onChange={update("value")} className="pp-input" data-testid="segment-value-input" placeholder={f.type === "coins" ? "e.g. 10" : f.type === "discount" ? "e.g. 10%" : ""} />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-[11px] uppercase tracking-widest text-brand-charcoal/55">Probability (%)</label>
            <input type="number" step="0.1" min="0" max="100" value={f.probability} onChange={update("probability")} className="pp-input" data-testid="segment-prob-input" />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-widest text-brand-charcoal/55">Sort order</label>
            <input type="number" value={f.sort_order} onChange={update("sort_order")} className="pp-input" data-testid="segment-sort-input" />
          </div>
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-widest text-brand-charcoal/55">Color</label>
          <div className="mt-1 flex flex-wrap gap-2">
            {PALETTE.map((c) => (
              <button type="button" key={c} onClick={() => setF((p) => ({ ...p, color: c }))} data-testid={`segment-color-${c}`} className={`h-8 w-8 rounded-full border-2 ${f.color === c ? "border-brand-charcoal" : "border-transparent"}`} style={{ background: c }} />
            ))}
            <input type="color" value={f.color} onChange={update("color")} className="h-8 w-12 rounded-md" />
          </div>
        </div>
        <div><label className="text-[11px] uppercase tracking-widest text-brand-charcoal/55">Description (optional)</label><input value={f.description || ""} onChange={update("description")} className="pp-input" data-testid="segment-desc-input" /></div>
        <div><label className="text-[11px] uppercase tracking-widest text-brand-charcoal/55">Image URL (optional)</label><input value={f.image_url || ""} onChange={update("image_url")} className="pp-input" /></div>
        <label className="inline-flex items-center gap-2 text-sm"><input type="checkbox" checked={!!f.is_active} onChange={update("is_active")} data-testid="segment-active-toggle" /> Active</label>

        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={onClose} className="btn-pill btn-ghost text-xs" data-testid="segment-cancel">Cancel</button>
          <button className="btn-pill btn-dark text-xs" data-testid="segment-save">Save</button>
        </div>
      </form>
    </div>
  );
}

// -------- Rules panel --------
function RulesPanel({ wheel, onReload }) {
  const [f, setF] = useState({ ...wheel });
  const [busy, setBusy] = useState(false);
  useEffect(() => { setF({ ...wheel }); }, [wheel.id]); // eslint-disable-line
  const upd = (k) => (e) => {
    const v = e.target?.type === "checkbox" ? e.target.checked : (e.target?.type === "number" ? parseFloat(e.target.value) : e.target.value);
    setF((p) => ({ ...p, [k]: v }));
  };
  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...f };
      delete payload.segments; delete payload.probability_valid; delete payload.probability_total;
      await api.put(`/admin/spin/wheels/${wheel.id}`, payload);
      toast.success("Rules saved");
      onReload();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl bg-white border border-brand-line p-5 space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div><label className="text-[11px] uppercase tracking-widest text-brand-charcoal/55">Name</label><input value={f.name || ""} onChange={upd("name")} className="pp-input" data-testid="wheel-name-input" /></div>
        <div><label className="text-[11px] uppercase tracking-widest text-brand-charcoal/55">Description</label><input value={f.description || ""} onChange={upd("description")} className="pp-input" data-testid="wheel-desc-input" /></div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div><label className="text-[11px] uppercase tracking-widest text-brand-charcoal/55">Theme color</label><input type="color" value={f.theme_color || "#FFC93C"} onChange={upd("theme_color")} className="h-10 w-16" data-testid="wheel-theme-color" /></div>
        <div><label className="text-[11px] uppercase tracking-widest text-brand-charcoal/55">Accent color</label><input type="color" value={f.accent_color || "#FF4F5A"} onChange={upd("accent_color")} className="h-10 w-16" data-testid="wheel-accent-color" /></div>
      </div>
      <div className="grid grid-cols-3 gap-4">
        <div><label className="text-[11px] uppercase tracking-widest text-brand-charcoal/55">Spins per day</label><input type="number" value={f.spins_per_day ?? 1} onChange={upd("spins_per_day")} className="pp-input" data-testid="wheel-per-day" /></div>
        <div><label className="text-[11px] uppercase tracking-widest text-brand-charcoal/55">Spins per week (0 = ∞)</label><input type="number" value={f.spins_per_week ?? 0} onChange={upd("spins_per_week")} className="pp-input" data-testid="wheel-per-week" /></div>
        <div><label className="text-[11px] uppercase tracking-widest text-brand-charcoal/55">Spins per month (0 = ∞)</label><input type="number" value={f.spins_per_month ?? 0} onChange={upd("spins_per_month")} className="pp-input" data-testid="wheel-per-month" /></div>
      </div>
      <div className="grid grid-cols-3 gap-4">
        <div><label className="text-[11px] uppercase tracking-widest text-brand-charcoal/55">Min Pop Coins</label><input type="number" value={f.min_pop_coins ?? 0} onChange={upd("min_pop_coins")} className="pp-input" data-testid="wheel-min-coins" /></div>
        <div><label className="text-[11px] uppercase tracking-widest text-brand-charcoal/55">Min Purchase (₹, info only)</label><input type="number" step="0.01" value={f.min_purchase_inr ?? 0} onChange={upd("min_purchase_inr")} className="pp-input" data-testid="wheel-min-purchase" /></div>
        <div><label className="text-[11px] uppercase tracking-widest text-brand-charcoal/55">Reward expiry (days)</label><input type="number" value={f.reward_expiry_days ?? 30} onChange={upd("reward_expiry_days")} className="pp-input" data-testid="wheel-expiry-days" /></div>
      </div>
      <div className="flex gap-6 items-center">
        <label className="inline-flex items-center gap-2 text-sm"><input type="checkbox" checked={!!f.is_active} onChange={upd("is_active")} data-testid="wheel-is-active" /> Active</label>
        <label className="inline-flex items-center gap-2 text-sm"><input type="checkbox" checked={!!f.is_visible} onChange={upd("is_visible")} data-testid="wheel-is-visible" /> Visible on site</label>
      </div>
      <div className="flex justify-end">
        <button disabled={busy} onClick={save} className="btn-pill btn-dark text-xs" data-testid="save-rules-btn"><Save size={12} /> {busy ? "Saving…" : "Save rules"}</button>
      </div>
    </div>
  );
}

// -------- Grants panel --------
function GrantsPanel({ wheel }) {
  const [grants, setGrants] = useState([]);
  const [memberId, setMemberId] = useState("");
  const [count, setCount] = useState(1);
  const [reason, setReason] = useState("manual");
  const [members, setMembers] = useState([]);

  const load = async () => {
    try {
      const [{ data: gs }, { data: ms }] = await Promise.all([
        api.get("/admin/spin/grants"),
        api.get("/admin/members"),
      ]);
      setGrants(gs); setMembers(ms);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const grant = async (e) => {
    e.preventDefault();
    if (!memberId) { toast.error("Select a member"); return; }
    try {
      await api.post("/admin/spin/grants", { member_id: memberId, wheel_id: wheel.id, count: parseInt(count), reason });
      toast.success("Free spins granted");
      setCount(1); setReason("manual"); setMemberId("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const revoke = async (g) => {
    if (!window.confirm("Revoke this grant?")) return;
    try { await api.delete(`/admin/spin/grants/${g.id}`); toast.success("Revoked"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleAccess = async (m, disabled) => {
    try { await api.patch(`/admin/spin/members/${m.member_id}/access`, { disabled }); toast.success(disabled ? "Access disabled" : "Access restored"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="rounded-2xl bg-white border border-brand-line p-5 space-y-5">
      <form onSubmit={grant} className="rounded-xl bg-brand-muted/40 p-4 grid grid-cols-1 sm:grid-cols-[1fr_100px_1fr_auto] gap-3 items-end" data-testid="grant-form">
        <div>
          <label className="text-[11px] uppercase tracking-widest text-brand-charcoal/55">Member</label>
          <select value={memberId} onChange={(e) => setMemberId(e.target.value)} className="pp-input" data-testid="grant-member-select">
            <option value="">Select…</option>
            {members.map((m) => <option key={m.id} value={m.id}>{m.name} ({m.member_code})</option>)}
          </select>
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-widest text-brand-charcoal/55">Count</label>
          <input type="number" min="1" value={count} onChange={(e) => setCount(e.target.value)} className="pp-input" data-testid="grant-count-input" />
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-widest text-brand-charcoal/55">Reason</label>
          <input value={reason} onChange={(e) => setReason(e.target.value)} className="pp-input" data-testid="grant-reason-input" placeholder="e.g. birthday" />
        </div>
        <button className="btn-pill btn-dark text-xs" data-testid="grant-submit"><Ticket size={12} /> Grant</button>
      </form>

      <div>
        <div className="text-[11px] uppercase tracking-widest text-brand-charcoal/55 mb-2">Active Grants</div>
        <ul className="space-y-2">
          {grants.length === 0 && <li className="text-sm text-brand-charcoal/45">No grants yet.</li>}
          {grants.map((g) => (
            <li key={g.id} className="flex items-center gap-3 rounded-xl border border-brand-line px-4 py-2 text-sm" data-testid={`grant-row-${g.id}`}>
              <Ticket size={14} className="text-brand-yellow" />
              <div className="flex-1">
                <div>{g.member_code} · {g.count} spin(s) · {g.reason}</div>
                <div className="text-[11px] text-brand-charcoal/55">granted {new Date(g.created_at).toLocaleDateString()} · consumed {g.consumed || 0}/{g.count}</div>
              </div>
              <button onClick={() => revoke(g)} className="text-red-600 text-xs" data-testid={`revoke-grant-${g.id}`}><Trash2 size={12} /></button>
            </li>
          ))}
        </ul>
      </div>

      <div>
        <div className="text-[11px] uppercase tracking-widest text-brand-charcoal/55 mb-2">Block a member from spinning</div>
        <ul className="grid sm:grid-cols-2 gap-2">
          {members.slice(0, 10).map((m) => (
            <li key={m.id} className="flex items-center justify-between rounded-xl border border-brand-line px-3 py-2 text-sm">
              <div>{m.name} <span className="font-mono text-[11px] text-brand-charcoal/55">{m.member_code}</span></div>
              {m.spin_disabled ? (
                <button onClick={() => toggleAccess({ member_id: m.id }, false)} className="text-xs text-emerald-700 inline-flex items-center gap-1" data-testid={`enable-spin-${m.member_code}`}><CheckCircle2 size={12} /> Re-enable</button>
              ) : (
                <button onClick={() => toggleAccess({ member_id: m.id }, true)} className="text-xs text-red-600 inline-flex items-center gap-1" data-testid={`disable-spin-${m.member_code}`}><Ban size={12} /> Block</button>
              )}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

// -------- History panel --------
function HistoryPanel({ wheel }) {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    api.get(`/admin/spin/history?wheel_id=${wheel.id}`).then(({ data }) => setRows(data)).catch(() => setRows([]));
  }, [wheel.id]);
  return (
    <div className="rounded-2xl bg-white border border-brand-line p-5">
      <div className="text-[11px] uppercase tracking-widest text-brand-charcoal/55 mb-3">Recent spins on this wheel</div>
      <table className="w-full text-sm">
        <thead className="text-[10px] uppercase tracking-widest text-brand-charcoal/55">
          <tr><th className="text-left py-2">When</th><th className="text-left py-2">Member</th><th className="text-left py-2">Outcome</th><th className="text-left py-2">Code</th></tr>
        </thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={4} className="py-8 text-center text-brand-charcoal/45">No spins yet.</td></tr>}
          {rows.map((r) => (
            <tr key={r.id} className="border-t border-brand-line" data-testid={`history-row-${r.id}`}>
              <td className="py-2 text-brand-charcoal/70">{new Date(r.created_at).toLocaleString()}</td>
              <td className="py-2 font-mono">{r.member_code}</td>
              <td className="py-2 inline-flex items-center gap-1">{r.is_win ? <Trophy size={12} className="text-brand-yellow" /> : <Frown size={12} className="text-brand-charcoal/40" />} {r.segment_label}</td>
              <td className="py-2 font-mono text-brand-charcoal/70">{r.reward_code || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
